import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CrmToaster, crmToast, FlagChips, IconCalendarUi, SearchInput, TrialDiscoveryBadge } from './crmUi'
import { Deal, DEALS, STAGE_MAP, dateFromDaysAgo } from './crmData'
import { useCrm } from './useCrm'
import { archiveDeal, getAdoptionSummary, getDeals, getSupportSummary, getUsageSummary, moveDealStage, patchQualification, resendTrialAlert, restoreDeal } from './crmApi'
import { LogCallModal } from './LogCallModal'
import { DealDetail } from './DealDetail'
import { DealQualification } from './DealQualification'
import { LeadRedirect } from './CrmLeadPage'
import { QuickBookButton } from './BookingModal'
import { attachLiveClock, tzAbbr, tzDiffers, viewerTz } from './localTime'
import { matchesTzFilter, setTzFilter, useTzFilter } from './tzFilter'

// Load the WHOLE board across pages (the API caps one request at 1000) so each column shows a true
// count, not just whatever landed in the newest 1000 deals.
const fetchAllDeals = async (params: Record<string, any> = {}): Promise<Deal[]> => {
  const size = 500
  const all: Deal[] = []
  for (let offset = 0; offset < 100000; offset += size) {
    const r = await getDeals({ ...params, limit: size, offset })
    const batch = (r.deals as Deal[]) || []
    all.push(...batch)
    if (batch.length < size) break
  }
  return all
}

// Cards shown per column page.
const COLUMN_PAGE_SIZE = 25

type SortKey = 'newest' | 'oldest' | 'az' | 'za' | 'value'
const SORT_LABELS: { key: SortKey; label: string }[] = [
  { key: 'newest', label: '↓ Newest' },
  { key: 'oldest', label: '↑ Oldest' },
  { key: 'az', label: 'A–Z' },
  { key: 'za', label: 'Z–A' },
  { key: 'value', label: '£ Value' },
]

/**
 * Lifecycle Board — the WeQuote trial→live customer lifecycle (Frame 45), and
 * THE CRM board (replaces the old pipeline board). It reads the same deal data
 * as before (live GET /api/crm/deals, falling back to the DEALS fixtures), and
 * maps each deal's existing fields into these six columns + card layouts.
 * Live-customer cards show REAL activity (GET /crm/usage/summary — counts for the
 * latest pushed period + last seen from active-sessions), and say "awaiting
 * WeQuote data" when none has landed; they no longer display an hours-per-week
 * figure invented from the lead score (WeQuote has no session timers). The module
 * strip and extension counts remain derived display-only values for now.
 * New signups (auto_new_lead) land in the first column, "On Trial". Cards open
 * the existing deal detail. Horizontal-only scroll (mouse wheel), fixed-height
 * columns with internal card scroll.
 */

// ─── Onboarding-status badge (derived from stage) ────────────────────────────
type Onb = 'cust_prog' | 'cust_done' | 'wq_prog' | 'wq_done'
const ONB: Record<Onb, { label: string; bg: string }> = {
  cust_prog: { label: 'CUSTOMER: IN PROGRESS', bg: '#64748B' },
  cust_done: { label: 'CUSTOMER: FINISHED', bg: '#F59E0B' },
  wq_prog: { label: 'WEQUOTE: IN PROGRESS', bg: '#2563EB' },
  wq_done: { label: 'WEQUOTE: FINISHED', bg: '#10B981' },
}
// Stage-derived badge FALLBACK. Corrected semantics: the pipeline stages describe the CUSTOMER's
// onboarding (auto_onboarding_* is their wizard — Zoho's 'Customer Finished' = onboarding_complete).
// WeQuote's own half only ever comes from the explicit onboarding_status.
const STAGE_ONB: Record<string, Onb> = {
  auto_new_lead: 'cust_prog',
  qualification: 'cust_prog',
  demo_scheduled: 'cust_prog',
  onboarding_in_progress: 'cust_prog',
  onboarding_complete: 'cust_done',
  setup_needed: 'cust_done',
  free_trial_expired: 'cust_done',
  trial_expired_contacted: 'cust_done',
  trial_reactivated: 'cust_prog',
  successfully_signed_up: 'cust_prog',
}
/** Explicit WeQuote onboarding status (events/sync) → badge. Wins over the stage-derived guess. */
const STATUS_ONB: Record<string, Onb> = {
  customer_in_progress: 'cust_prog',
  customer_finished: 'cust_done',
  wequote_in_progress: 'wq_prog',
  wequote_finished: 'wq_done',
}
const onbOf = (d: Deal): Onb => (d.onboardingStatus && STATUS_ONB[d.onboardingStatus]) || STAGE_ONB[d.stage] || 'cust_prog'

// ─── Module adoption strip (Live companies) — REAL, as of Phase 2.3 ──────────
// This strip used to be a lie: the colours were computed from the characters of the
// deal's ID. It now shows what the customer actually does, from GET /crm/adoption/summary
// — a module is "in use" because they used it, not because someone ticked a box.
type ModStatus = 'using' | 'setting_up' | 'not_using'
const MOD_COLOR: Record<ModStatus, string> = { using: '#22C55E', setting_up: '#F59E0B', not_using: '#EF4444' }

/** Adoption for one deal (GET /crm/adoption/summary). */
interface AdoptionModule { key: string; label: string; state: 'in_use' | 'set_up_unused' | 'dormant' | 'never' | 'unknown'; current: number; mismatch: string | null }
interface AdoptionRow { hasUsage: boolean; seeded: boolean; modules: AdoptionModule[]; inUse: number; setUpUnused: number; dormant: number; effectiveLive: number }
type AdoptionMap = Record<string, AdoptionRow>

const STATE_TO_STRIP: Record<AdoptionModule['state'], ModStatus> = {
  in_use: 'using',
  set_up_unused: 'setting_up', // switched on, never touched — the stalled rollout
  dormant: 'setting_up',       // they used it and stopped — the drop-off
  never: 'not_using',
  unknown: 'not_using',        // no usage feed — the strip isn't rendered in this case
}
const STATE_TITLE: Record<AdoptionModule['state'], string> = {
  in_use: 'In use this period',
  set_up_unused: 'Switched on but never used — stalled rollout',
  dormant: 'Used before, nothing this period — abandoned',
  never: 'Never used',
  unknown: 'No usage data for this account — adoption unknown',
}

// ─── Card model + column mapping ─────────────────────────────────────────────
type Variant = 'cold' | 'trial' | 'trial_ended' | 'trial_extended' | 'trial_demo' | 'live_demo' | 'live' | 'cancelled'

/** A "Cold Lead" is one that did NOT come in through the trial-account API —
 * i.e. an uploaded / event lead (tagged on import) that hasn't started a trial.
 * These belong in the Cold Leads column, not On Trial. */
const isColdLead = (d: Deal): boolean =>
  d.trialStartedDays == null &&
  !!d.tags &&
  (d.tags.includes('Cold Lead') || d.tags.includes('Imported') || d.tags.includes('Event Lead'))

interface LCard {
  deal: Deal
  company: string
  user: string
  price: number
  live: boolean
  /** Not on a trial (yet) — an active/event lead. No trial bar, no ON TRIAL pill. */
  preTrial?: boolean
  onb: Onb
  paid?: string
  paidBad?: boolean
  signUp?: string
  trialUsed?: number
  trialTotal?: number
  endedDate?: string
  endedDaysAgo?: number
  firstTrialEnd?: string
  latestExtension?: string
  totalExtensions?: number
  /** Reactivated trials with a REAL synced end date (Lee, 19 Aug 2026). */
  reactEnded?: string
  reactEndedDaysAgo?: number
  reactDaysLeft?: number
  goLive?: string
  lastUsage?: string
  avgUsage?: string
  /** Real module adoption (Phase 2.3) — derived from usage, not from the deal id. */
  adoption?: AdoptionRow
  /** Live support history for this lead (Phase 2.1) — joined, not stored. */
  support?: SupportRow
  /** Real Calendly demo details (latest booking) — shown on the demo columns' cards. */
  demoInfo?: { eventName: string; when: string; inviteeName?: string; inviteeEmail?: string; meetUrl?: string }
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n))

function columnForDeal(d: Deal): string | null {
  // A booked demo (Calendly) pulls the card into the matching demo column without touching the
  // lead's lifecycle stage — but only while the meeting is UPCOMING (2h grace after it ends).
  // Once the demo has happened, the card returns to its lifecycle column and the meeting lives on
  // in the deal's Meetings tab. Cold/uploaded leads are NOT on this board (they live on the Leads
  // page). On-trial splits by ONBOARDING state: "WeQuote: Finished" (onboarding_complete) →
  // Onboarded On-Trial; everything earlier → Free Trial Sign Up.
  // "Booked" pull-in window = until one hour after the scheduled start (same boundary as the
  // HELD divider and the Meet-button removal). Stage demo_scheduled now uses the SAME window —
  // past it, an unrestaged lead lands in Nurture (Lee, 19 Aug 2026).
  const hasBookedDemo = !!d.demoAt && Date.now() < new Date(d.demoAt).getTime() + 3600_000
  // PRD v1.0 qualification columns — mirrors the server's segmentOfDeal precedence:
  // terminal beats everything; Nurture (non-live) gets its own column; Qualified
  // pre-trial leads go to Qualified (demo pull-in still wins); trial/live lifecycle
  // columns take over after that.
  // BOARD REMODEL (Lee, 9 Oct 2026): demos and trials split by qualification —
  // mirrors the server's segmentOfDeal exactly.
  const q = d.qualificationStatus === 'qualified'
  const demoSeg = q ? 'demos_qualified' : 'demos_qualifying'
  const trialSeg = q ? 'trials_qualified' : 'trials_qualifying'
  const TERMINAL_STAGES = ['cancelled_subscription', 'closed_lost', 'archived', 'cancellation_requested', 'churned_in_conversation']
  // WEBSITE FORM BADGES (Lee, 3 Nov 2026): a Free Trial REQUESTED lead sits in
  // the trials column; a DEMO BOOKING PENDING lead sits with the demos —
  // before any account/booking exists. Price Enquired is its own stage column.
  if (!TERMINAL_STAGES.includes(d.stage) && !d.archivedAt) {
    // Price Enquired column REMOVED (Lee, 4 Nov 2026 — one route now: the
    // 15-min discovery call). A stray price_enquired lead rides as a demo-
    // qualifying lead so it can never go invisible.
    if (d.stage === 'price_enquired') return demoSeg
    if (d.formStatus === 'trial_requested') return trialSeg
    if (d.formStatus === 'demo_pending' && !hasBookedDemo) return demoSeg
  }
  if (!TERMINAL_STAGES.includes(d.stage)) {
    if (d.qualificationStatus === 'nurture' && d.stage !== 'successfully_signed_up') return 'nurture'
    if (q && ['auto_new_lead', 'qualification'].includes(d.stage) && d.trialStartedDays == null && isColdLead(d)) {
      return 'demos_qualified' // qualified pre-trial event/cold lead — came via a demo
    }
  }
  switch (d.stage) {
    case 'successfully_signed_up':
      return 'all_live'
    case 'demo_scheduled':
      // Live bookings (upcoming / within 1h of start) + all QUALIFIED demo leads.
      // Held-but-unqualified auto-qualifies within the hour (server sweep);
      // the nurture fallback keeps the 19-Aug no-stale-cards rule meanwhile.
      if (!d.demoAt || Date.now() < new Date(d.demoAt).getTime() + 3600_000) return demoSeg
      return q ? 'demos_qualified' : 'nurture'
    case 'free_trial_expired':
      return hasBookedDemo ? demoSeg : 'trial_ended'
    case 'trial_expired_contacted':
      return hasBookedDemo ? demoSeg : 'trial_expired_contacted'
    case 'cancelled_subscription':
    case 'closed_lost':
    case 'archived':
    case 'cancellation_requested':
    case 'churned_in_conversation':
      return 'cancelled'
    case 'trial_reactivated':
      return d.onboardingStatus === 'wequote_finished' ? 'onboarded_trial' : trialSeg
    case 'auto_new_lead':
    case 'qualification':
      // Uploaded / event leads → not on this board (Leads page).
      return isColdLead(d) ? null : trialSeg
    case 'onboarding_in_progress':
    case 'onboarding_complete':
    case 'setup_needed':
      if (d.onboardingStatus === 'wequote_finished') return 'onboarded_trial'
      return trialSeg
    default:
      return null
  }
}

/** Latest-period usage + presence for a deal (GET /crm/usage/summary). */
interface UsageRow { total: number | null; activeDays: number | null; periodDays: number; seeded?: boolean; lastSeenAt?: string | null; lastSeenDays?: number | null; activeUsers?: number }
type UsageMap = Record<string, UsageRow>

/** Support history for a deal (GET /crm/support/summary) — Phase 2.1. */
interface SupportRow { openTickets: number; ticketsTotal: number; lastTicketDays: number | null; chatsTotal: number; lastChatDays: number | null; lastContactDays: number | null }
type SupportMap = Record<string, SupportRow>

const agoLabel = (days?: number | null) =>
  days == null ? undefined : days <= 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days}d ago`

function toCard(d: Deal, usage?: UsageRow, support?: SupportRow, adoption?: AdoptionRow): LCard {
  const live = d.stage === 'successfully_signed_up'
  const onb = onbOf(d)
  const startAgo = d.trialStartedDays ?? d.createdDays
  // Real Calendly meeting details for the demo columns.
  const demoInfo = d.demoAt
    ? {
        eventName: d.demoEventName || 'Demo',
        when: `${new Date(d.demoAt).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} · ${new Date(d.demoAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}${d.demoEndAt ? `–${new Date(d.demoEndAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : ''}`,
        inviteeName: d.demoInviteeName || undefined,
        inviteeEmail: d.demoInviteeEmail || undefined,
        meetUrl: d.demoMeetUrl,
      }
    : undefined
  // A lead that has NOT started a trial (e.g. created from a Calendly booking by an unknown
  // booker) is an ACTIVE LEAD — it must not wear the ON TRIAL pill or a trial progress bar.
  // It becomes On-Trial/Live only when WeQuote's trial/paid events match it (email or domain).
  const preTrial = !live && d.trialStartedDays == null && ['auto_new_lead', 'qualification', 'demo_scheduled'].includes(d.stage)
  const base: LCard = {
    deal: d, company: d.company, user: d.contact, price: live ? d.amount : 0, live, preTrial, onb,
    support: support && (support.ticketsTotal || support.chatsTotal) ? support : undefined,
    demoInfo,
  }
  if (preTrial) return { ...base, signUp: dateFromDaysAgo(d.createdDays) }
  // Cold uploaded/event leads — no trial figures, just capture details.
  if (!live && isColdLead(d) && (d.stage === 'auto_new_lead' || d.stage === 'qualification')) {
    return { ...base, signUp: dateFromDaysAgo(d.createdDays) }
  }
  if (live) {
    // Activity is what WeQuote actually reports — counts over a period, not hours.
    // Until the first period is pushed we say so rather than inventing a figure
    // (this card used to show "N Hours/Week" derived from the lead score).
    const activity =
      usage?.total != null
        ? `${usage.total.toLocaleString()} actions${usage.activeDays != null ? ` · active ${usage.activeDays}/${usage.periodDays}d` : ''}${usage.seeded ? ' · demo' : ''}`
        : 'Awaiting WeQuote data'
    return {
      ...base,
      paid: d.paymentFailed ? 'Payment failed' : 'Paid up to date',
      paidBad: !!d.paymentFailed,
      // Go-live is WeQuote's activation date. If WeQuote didn't send one, say Unknown —
      // falling back to created_at showed the IMPORT date (today) as a fake go-live.
      goLive: d.activatedDays != null ? dateFromDaysAgo(d.activatedDays) : 'Unknown',
      // HONESTY (Lee, 30 Jul 2026): last usage comes ONLY from real customer signals
      // (presence, then the usage period). The old fallback to CRM last_activity_at
      // showed OUR sync/notes timestamps as customer activity — cards claimed "Today"
      // for companies Customer Success correctly rated dormant/High Risk.
      lastUsage:
        agoLabel(usage?.lastSeenDays) ??
        (usage ? (usage.total ? 'Active this period' : 'None this period') : 'No usage data'),
      avgUsage: activity,
      adoption,
    }
  }
  if (d.stage === 'free_trial_expired') {
    // Prefer WeQuote's REAL trial-end date (trialEndsDays, once their API sends it); fall back to
    // trial start + the ~14-day trial so it still reads as a past date, never "today".
    const endedAgo = d.trialEndsDays != null ? Math.max(0, d.trialEndsDays) : Math.max(0, startAgo - 14)
    return { ...base, endedDate: dateFromDaysAgo(endedAgo), endedDaysAgo: endedAgo, trialUsed: 14, trialTotal: 14 }
  }
  if (d.stage === 'trial_reactivated') {
    const used = clamp(startAgo, 15, 56)
    const react = {
      ...base,
      signUp: dateFromDaysAgo(d.createdDays),
      firstTrialEnd: dateFromDaysAgo(startAgo - 14),
      latestExtension: dateFromDaysAgo(d.lastActivityDays),
      totalExtensions: Math.max(1, Math.round((used - 14) / 14)),
    }
    // HONESTY (Lee, 19 Aug 2026): "56/56 Days" was an ASSUMPTION (14-day trial +
    // extensions, capped at 56) from before WeQuote synced the real trial end.
    // With trial_ends_at on the deal, show the truth instead: expired → say so
    // in red; still running → days left on the reactivated trial.
    if (d.trialEndsDays != null) {
      if (d.trialEndsDays > 0) return { ...react, reactEnded: dateFromDaysAgo(d.trialEndsDays), reactEndedDaysAgo: d.trialEndsDays }
      return { ...react, reactDaysLeft: Math.max(0, -d.trialEndsDays) }
    }
    return { ...react, trialUsed: used, trialTotal: 56 }
  }
  // On-Trial card. With WeQuote's real trial-end date the bar shows the true window
  // (elapsed / actual length); otherwise the assumed 14-day trial.
  if (d.trialEndsDays != null) {
    const total = Math.max(1, startAgo - d.trialEndsDays)
    const used = clamp(startAgo - Math.max(0, d.trialEndsDays), 1, total)
    return { ...base, signUp: dateFromDaysAgo(d.createdDays), trialUsed: used, trialTotal: total }
  }
  return { ...base, signUp: dateFromDaysAgo(d.createdDays), trialUsed: clamp(startAgo, 1, 14), trialTotal: 14 }
}

interface Column { key: string; label: string; color: string; variant: Variant }
const COLUMNS: Column[] = [
  // Cold Leads intentionally NOT on this board (they live on the Leads page). On-trial is split by
  // onboarding state: pre-"WeQuote: Finished" → Free Trial Sign Up; "WeQuote: Finished" → Onboarded.
  // One merged "Demos Booked" column (sales) covers trial AND live companies — the card body shows
  // the right details either way. Trial Extended removed: extensions happen back-end with no
  // signal, so there's nothing truthful to differentiate on.
  // PRD v1.0 (Lee, 6 Aug 2026): Qualified = pre-trial leads that passed all six
  // gates — the true top of the pipeline. Nurture = still a fit, timeline lost /
  // unresponsive; out of the active pipeline but recyclable.
  // BOARD REMODEL (Lee, 9 Oct 2026): demos/trials split into Qualifying →
  // Qualified; Qualify button on the Qualifying cards opens the questions
  // popup. Held demos and (backfilled) current trials auto-qualify.
  { key: 'demos_qualifying', label: 'Demos — Qualifying', color: '#2563EB', variant: 'trial_demo' },
  { key: 'trials_qualifying', label: 'Trials — Qualifying', color: '#64748B', variant: 'trial' },
  { key: 'demos_qualified', label: 'Demos — Qualified', color: '#2F6FB0', variant: 'trial_demo' },
  { key: 'trials_qualified', label: 'Trials — Qualified', color: '#16A34A', variant: 'trial' },
  { key: 'onboarded_trial', label: 'Trials — Onboarded', color: '#0891B2', variant: 'trial' },
  { key: 'all_live', label: 'All Live Companies', color: '#14B8A6', variant: 'live' },
  { key: 'trial_ended', label: 'Trial Expired', color: '#EF4444', variant: 'trial_ended' },
  // Manual sales state — cards are DRAGGED here from Trial Expired once the team has reached out.
  { key: 'trial_expired_contacted', label: 'Trial Expired — Contacted', color: '#FB923C', variant: 'trial_ended' },
  { key: 'nurture', label: 'Nurture', color: '#6366F1', variant: 'cold' },
  // Cancelled/Churned fills from status + the Zoho backfill (separate track); Archived shows
  // soft-deleted records. Neither contributes to any metric elsewhere.
  { key: 'cancelled', label: 'Cancelled/Churned', color: '#6B7280', variant: 'cancelled' },
  { key: 'archived_col', label: 'Archived', color: '#9CA3AF', variant: 'cancelled' },
]

// ─── SALES BOARD (Lee, 12 Oct 2026): the pre-remodel layout, kept alive as a
// second page (/crm/sales-board). Same cards, same data — only the column
// model differs. One merged Demos Booked column, trials NOT split by
// qualification. No Qualify button here (that's the Qualification Board's
// workflow). NO Qualified column either (Lee, 14 Oct 2026) — qualification
// does not affect this board. Rendered via CrmSalesBoard below.
const COLUMNS_LEGACY: Column[] = [
  { key: 'free_trial_signup', label: 'Free Trial Sign Up', color: '#334155', variant: 'trial' },
  { key: 'onboarded_trial', label: 'Onboarded On-Trial', color: '#0891B2', variant: 'trial' },
  { key: 'trial_ended', label: 'Trial Expired', color: '#EF4444', variant: 'trial_ended' },
  { key: 'trial_expired_contacted', label: 'Trial Expired — Contacted', color: '#FB923C', variant: 'trial_ended' },
  { key: 'demos', label: 'Demos Booked', color: '#2F6FB0', variant: 'trial_demo' },
  { key: 'all_live', label: 'All Live Companies', color: '#14B8A6', variant: 'live' },
  { key: 'nurture', label: 'Nurture', color: '#6366F1', variant: 'cold' },
  { key: 'cancelled', label: 'Cancelled/Churned', color: '#6B7280', variant: 'cancelled' },
  { key: 'archived_col', label: 'Archived', color: '#9CA3AF', variant: 'cancelled' },
]

/** The EXACT pre-remodel (pre-9-Oct-2026) column mapping, for the Sales Board. */
function legacyColumnForDeal(d: Deal): string | null {
  const hasBookedDemo = !!d.demoAt && Date.now() < new Date(d.demoAt).getTime() + 3600_000
  const TERMINAL_STAGES = ['cancelled_subscription', 'closed_lost', 'archived', 'cancellation_requested', 'churned_in_conversation']
  // WEBSITE FORM BADGES (Lee, 3 Nov 2026) — see columnForDeal.
  if (!TERMINAL_STAGES.includes(d.stage) && !d.archivedAt) {
    // Price Enquired column REMOVED (Lee, 4 Nov 2026) — strays ride in Demos.
    if (d.stage === 'price_enquired') return 'demos'
    if (d.formStatus === 'trial_requested') return 'free_trial_signup'
    if (d.formStatus === 'demo_pending' && !hasBookedDemo) return 'demos'
  }
  if (!TERMINAL_STAGES.includes(d.stage)) {
    if (d.qualificationStatus === 'nurture' && d.stage !== 'successfully_signed_up') return 'nurture'
    // NO qualification routing here (Lee, 14 Oct 2026): the Qualified column
    // is gone from the Sales Board — a lead with a live demo booking still
    // lands in Demos Booked; otherwise the stage alone decides.
    if (hasBookedDemo && ['auto_new_lead', 'qualification'].includes(d.stage)) return 'demos'
  }
  switch (d.stage) {
    case 'successfully_signed_up':
      return 'all_live'
    case 'demo_scheduled':
      // Demos Booked only holds LIVE bookings (upcoming / within 1h of start);
      // past that, an unrestaged lead lands in Nurture (Lee, 19 Aug 2026).
      return !d.demoAt || Date.now() < new Date(d.demoAt).getTime() + 3600_000 ? 'demos' : 'nurture'
    case 'free_trial_expired':
      return hasBookedDemo ? 'demos' : 'trial_ended'
    case 'trial_expired_contacted':
      return hasBookedDemo ? 'demos' : 'trial_expired_contacted'
    case 'cancelled_subscription':
    case 'closed_lost':
    case 'archived':
    case 'cancellation_requested':
    case 'churned_in_conversation':
      return 'cancelled'
    case 'trial_reactivated':
      return d.onboardingStatus === 'wequote_finished' ? 'onboarded_trial' : 'free_trial_signup'
    case 'auto_new_lead':
    case 'qualification':
      return isColdLead(d) ? null : 'free_trial_signup'
    case 'onboarding_in_progress':
    case 'onboarding_complete':
    case 'setup_needed':
      if (d.onboardingStatus === 'wequote_finished') return 'onboarded_trial'
      return 'free_trial_signup'
    default:
      return null
  }
}

// ─── Bits ────────────────────────────────────────────────────────────────────
const gbp = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** Website-form badge on cards (Lee, 3 Nov 2026). */
const FormChip: React.FC<{ d: Deal }> = ({ d }) =>
  d.formStatus === 'trial_requested' ? (
    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: '#7C3AED' }} title={`Free Trial requested on the website${d.formStatusAt ? ` · ${new Date(d.formStatusAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''} — create their WeQuote account, it binds automatically`}>REQUESTED</span>
  ) : d.formStatus === 'demo_pending' ? (
    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: '#D97706' }} title={`Website demo form filled${d.formStatusAt ? ` · ${new Date(d.formStatusAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''} — Calendly booking not made yet`}>DEMO BOOKING PENDING</span>
  ) : null

const OnbBadge: React.FC<{ onb: Onb }> = ({ onb }) => (
  <span className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide text-white whitespace-nowrap" style={{ background: ONB[onb].bg }}>
    {ONB[onb].label}
  </span>
)
/** The lead's CURRENT local time, on every card (Lee, 23 Sep 2026) — "is it a
 * sane hour to ring them" at a glance. The zone is the SERVER-resolved
 * deal.localTz (state → US/CA area code → phone dial code → country), so US
 * leads land in their real state zone instead of the old GB-defaulted country
 * field. Runs in real time — seconds tick every second via useClock. */
const LeadLocalTime: React.FC<{ tz?: string | null; country?: string | null }> = React.memo(({ tz: tzProp }) => {
  // ONLY the API-sent timezone (Lee, 23 Sep 2026) — no chip until WeQuote
  // provides one; never a guess.
  const tz = tzProp || ''
  const clockRef = useRef<HTMLSpanElement>(null)
  // Live clock WITHOUT React re-renders: one global interval paints every
  // registered span directly (see localTime.ts — the v1 per-chip setState
  // froze the board at ~1,000 cards).
  useEffect(() => (tz ? attachLiveClock(clockRef.current, tz) : undefined), [tz])
  if (!tz) return null
  const differs = tzDiffers(tz, viewerTz())
  const city = tz.split('/').pop()?.replace(/_/g, ' ')
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border tabular-nums ${differs ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-surface-secondary border-border text-txt-tertiary'}`}
      title={`Local time for the lead (${tz})${differs ? ' — different from yours' : ' — same as yours'}`}
    >
      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <span ref={clockRef} /> {tzAbbr(tz)}{differs && city ? ` · ${city}` : ''}
    </span>
  )
})

/* HIERARCHY PASS (Lee, 21 Oct 2026): labels fall back (small caps, tertiary),
   values step forward (semibold, primary) — the old same-size label/value
   pairs read as a flat wall. */
const Row: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary shrink-0">{label}</span>
    <span className="text-[12.5px] text-txt-primary font-semibold text-right min-w-0 truncate">{value}</span>
  </div>
)

/* TOUCH STRIP (Lee, 21 Oct 2026): calls + meetings at a glance on every card,
   with on-card call logging — shared by sales + qualification boards. */
const TouchStrip: React.FC<{ deal: any; company: string }> = ({ deal, company }) => {
  const [logOpen, setLogOpen] = useState(false)
  const calls = Number(deal.callsCount || 0)
  const meets = Number(deal.meetingsCount || 0)
  return (
    <div className="flex items-center gap-3 rounded-lg bg-surface-secondary/60 px-2.5 py-1.5" onClick={(e) => e.stopPropagation()}>
      <span className="inline-flex items-center gap-1.5 min-w-0" title={`${calls} call${calls === 1 ? '' : 's'} logged`}>
        <svg className={`w-3.5 h-3.5 ${calls ? 'text-emerald-600' : 'text-txt-tertiary/60'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
        <span className={`text-[11px] font-bold tabular-nums ${calls ? 'text-txt-primary' : 'text-txt-tertiary'}`}>{calls}</span>
        <span className="text-[10px] text-txt-tertiary">call{calls === 1 ? '' : 's'}</span>
      </span>
      <span className="w-px h-3.5 bg-border" />
      <span className="inline-flex items-center gap-1.5 min-w-0" title={`${meets} meeting${meets === 1 ? '' : 's'} on record`}>
        <svg className={`w-3.5 h-3.5 ${meets ? 'text-brand-600' : 'text-txt-tertiary/60'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5l4.72-2.36a.75.75 0 011.03.67v6.38a.75.75 0 01-1.03.67L15 13.5m-12-3A2.25 2.25 0 015.25 8.25h5.5A2.25 2.25 0 0113 10.5v3a2.25 2.25 0 01-2.25 2.25h-5.5A2.25 2.25 0 013 13.5v-3z" /></svg>
        <span className={`text-[11px] font-bold tabular-nums ${meets ? 'text-txt-primary' : 'text-txt-tertiary'}`}>{meets}</span>
        <span className="text-[10px] text-txt-tertiary">meeting{meets === 1 ? '' : 's'}</span>
      </span>
      <button
        onClick={() => setLogOpen(true)}
        className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors shrink-0"
        title="Log a phone call against this lead"
      >
        + Log call
      </button>
      {logOpen && <LogCallModal dealId={deal.id} dealName={company} onClose={() => setLogOpen(false)} />}
    </div>
  )
}
const TrialBar: React.FC<{ used: number; total: number; ended?: boolean; firstTrial?: number }> = ({ used, total, ended, firstTrial }) => {
  const pct = clamp(Math.round((used / total) * 100), 0, 100)
  if (ended) return <div className="h-1.5 rounded-full" style={{ background: '#EF4444' }} />
  if (firstTrial != null) {
    const redPct = clamp((firstTrial / total) * 100, 0, 100)
    const greenPct = Math.max(0, pct - redPct)
    return (
      <div className="h-1.5 rounded-full bg-surface-tertiary overflow-hidden flex">
        <div style={{ width: `${redPct}%`, background: '#EF4444' }} />
        <div style={{ width: `${greenPct}%`, background: '#22C55E' }} />
      </div>
    )
  }
  return (
    <div className="h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#22C55E' }} />
    </div>
  )
}
/**
 * The module strip, now telling the truth (Phase 2.3). Each block is what the customer
 * ACTUALLY does with that module this period. Amber is the one to look at: switched on
 * and never used, or used and then abandoned.
 */
const ModuleStrip: React.FC<{ adoption: AdoptionRow }> = ({ adoption }) => {
  if (!adoption.hasUsage) {
    return <p className="text-[10px] text-txt-tertiary italic">No usage data yet — module adoption unknown</p>
  }
  const stalled = adoption.modules.filter((m) => m.mismatch === 'live_but_unused').length
  return (
    <div>
      <div className="grid grid-cols-6 gap-1.5">
        {adoption.modules.map((m) => (
          <div key={m.key} className="flex flex-col items-center gap-1" title={`${m.label}: ${STATE_TITLE[m.state]}${m.current ? ` (${m.current} actions)` : ''}`}>
            <span className="w-full h-6 rounded-md" style={{ background: MOD_COLOR[STATE_TO_STRIP[m.state]] }} />
            <span className="text-[9px] text-txt-tertiary leading-tight text-center truncate w-full">{m.label}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-txt-tertiary mt-1">
        {adoption.inUse} in use
        {adoption.dormant > 0 && <span style={{ color: '#B45309' }}> · {adoption.dormant} abandoned</span>}
        {stalled > 0 && <span style={{ color: '#B45309' }}> · {stalled} switched on, never used</span>}
        {adoption.seeded && <span className="opacity-70"> · demo</span>}
      </p>
    </div>
  )
}
const Avatar: React.FC<{ name: string; logoUrl?: string }> = ({ name, logoUrl }) => {
  const [broken, setBroken] = useState(false)
  if (logoUrl && !broken)
    return (
      <img
        src={logoUrl}
        alt={name}
        className="w-11 h-11 rounded-lg shrink-0 object-contain bg-white border border-border"
        onError={() => setBroken(true)}
        loading="lazy"
      />
    )
  return (
    <span className="w-11 h-11 rounded-lg shrink-0 flex items-center justify-center text-white text-base font-bold" style={{ background: '#2563EB' }}>
      {name.charAt(0).toUpperCase()}
    </span>
  )
}

/** "Next Task" footer — the one task marked as next in the deal's Tasks tab, under a divider.
 * Clicking it opens the deal straight on the Tasks tab (stops the card's own click). */
const NextTaskLine: React.FC<{ task?: string; onOpenTasks?: () => void }> = ({ task, onOpenTasks }) =>
  !task ? null : (
    <div
      className={`border-t border-border pt-2 ${onOpenTasks ? 'cursor-pointer group' : ''}`}
      title={`${task} — click to open the Tasks tab`}
      onClick={onOpenTasks ? (e) => { e.stopPropagation(); onOpenTasks() } : undefined}
    >
      <p className="text-[12px] text-txt-secondary truncate group-hover:text-brand-600 transition-colors">
        <span className="font-bold text-brand-600 uppercase tracking-wide text-[10px] mr-1">Next Task:</span>
        {task}
      </p>
    </div>
  )

/** Google Drive company-folder chip (Lee, 7 Oct 2026) — the link is written onto the
 *  lead by the daily CRM↔Drive sync; shown on every card variant when present. */
const DriveChip: React.FC<{ url?: string }> = ({ url }) =>
  url ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 hover:bg-blue-100"
      title="Open this company's Google Drive folder"
    >
      📁 Drive
    </a>
  ) : null

/** The board's QUALIFY button (Lee, 9 Oct 2026) — sits with the card actions
 *  on Qualifying columns; opens the qualification-questions popup. */
const QualifyBtn: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick() }}
    className="px-2 py-1 rounded-lg text-[11px] font-bold text-white whitespace-nowrap"
    style={{ background: '#16A34A' }}
    title="Answer the qualifying questions and qualify this lead"
  >
    Qualify
  </button>
)

const Card: React.FC<{ c: LCard; variant: Variant; onOpen: () => void; onOpenTasks?: () => void; onQualify?: () => void }> = ({ c, variant, onOpen, onOpenTasks, onQualify }) => {
  const demo = variant === 'trial_demo' || variant === 'live_demo'

  // Cancelled/Churned + Archived card — NO trial pill, NO trial bar (a cancelled deal
  // wearing "ON TRIAL 14/14 days" was a lie). Shows what actually matters: the exact
  // stage, the money that walked, sign-up and last-activity dates.
  if (variant === 'cancelled') {
    const d = c.deal
    const stageMeta = STAGE_MAP[d.stage]
    return (
      <div onClick={onOpen} className="rounded-xl bg-surface border border-border p-4 space-y-3 hover:shadow-sm hover:border-brand-300 transition-all cursor-pointer opacity-90">
        <div className="flex items-start gap-3">
          <Avatar name={c.company} logoUrl={d.logoUrl} />
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-txt-primary truncate">{c.company}</p>
            <p className="text-[13px] text-txt-tertiary truncate">{c.user || '—'}</p>
            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: stageMeta?.color || '#6B7280' }}>
                {stageMeta?.label || d.stage.replace(/_/g, ' ')}
              </span>
              {d.cardRemoved && <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase text-white" style={{ background: '#DC2626' }}>CARD REMOVED</span>}
              <LeadLocalTime tz={d.localTz} country={d.country} />
              <FormChip d={d} /><FlagChips flags={d.flags} />
              <DriveChip url={(d as any).driveFolderUrl} />
            </div>
          </div>
        </div>
        <div className="space-y-2 border-t border-border pt-3">
          {d.mrr ? <Row label="MRR lost" value={<span className="text-red-600 font-semibold">£{d.mrr}/mo</span>} /> : null}
          <Row label="Signed up" value={dateFromDaysAgo(d.createdDays)} />
          <Row label="Last activity" value={dateFromDaysAgo(d.lastActivityDays ?? 0)} />
          <Row label="Source" value={d.source || '—'} />
        </div>
        <TouchStrip deal={d} company={c.company} />
        <NextTaskLine task={d.nextTask} onOpenTasks={onOpenTasks} />
      </div>
    )
  }

  // Cold Leads card — compact, no trial figures.
  if (variant === 'cold') {
    const d = c.deal
    const hasDemo = d.demoScheduledDays != null
    return (
      <div onClick={onOpen} className="rounded-xl bg-surface border border-border p-4 space-y-3 hover:shadow-sm hover:border-brand-300 transition-all cursor-pointer">
        <div className="flex items-start gap-3">
          <Avatar name={c.company} logoUrl={c.deal.logoUrl} />
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-txt-primary truncate">{c.company}</p>
            <p className="text-[13px] text-txt-tertiary truncate">{c.user || '—'}</p>
            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
              <span
                className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white"
                style={{ background: d.qualificationStatus === 'qualified' ? '#16A34A' : d.qualificationStatus === 'nurture' ? '#6366F1' : '#2563EB' }}
              >
                {d.qualificationStatus === 'qualified' ? 'QUALIFIED' : d.qualificationStatus === 'nurture' ? 'NURTURE' : 'COLD LEAD'}
              </span>
              {d.qualificationStatus === 'qualified' && d.leadGrade && (
                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-white" title="Priority grade — never affects the pipeline gate">{d.leadGrade}</span>
              )}
              <LeadLocalTime tz={d.localTz} country={d.country} />
              <FormChip d={d} /><FlagChips flags={d.flags} />
              <DriveChip url={(d as any).driveFolderUrl} />
            </div>
          </div>
          <QuickBookButton dealId={d.id} dealName={c.company} compact />
        </div>
        <div className="space-y-2 border-t border-border pt-3">
          {d.opportunity && <Row label="Show / Event" value={<span className="truncate">{d.opportunity}</span>} />}
          <Row label="Source" value={d.source} />
          <Row label="Demo" value={hasDemo ? <span className="text-brand-600">{(d.demoScheduledDays ?? 0) < 0 ? `In ${Math.abs(d.demoScheduledDays ?? 0)}d` : dateFromDaysAgo(d.demoScheduledDays ?? 0)}</span> : <span className="text-txt-tertiary">No demo</span>} />
          <Row label="Added" value={dateFromDaysAgo(d.createdDays)} />
        </div>
        <TouchStrip deal={d} company={c.company} />
        <NextTaskLine task={d.nextTask} onOpenTasks={onOpenTasks} />
      </div>
    )
  }

  return (
    <div onClick={onOpen} className="rounded-xl bg-surface border border-border p-4 space-y-4 hover:shadow-sm hover:border-brand-300 transition-all cursor-pointer">
      <div className="flex items-start gap-3">
        <Avatar name={c.company} logoUrl={c.deal.logoUrl} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[15px] font-extrabold leading-tight text-txt-primary truncate">{c.company}</p>
              {/* Cancellation chip (Lee, 30 Oct 2026): red = requested, amber = pending-with-leave-date. */}
              {(c.deal as any).cancelEffectiveAt ? (
                <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-amber-100 text-amber-800 border border-amber-300" title={(c.deal as any).cancelRequestReason || undefined}>⏳ LEAVES {new Date((c.deal as any).cancelEffectiveAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase()}</span>
              ) : (c.deal as any).cancelRequestedAt ? (
                <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-red-100 text-red-700 border border-red-300" title={(c.deal as any).cancelRequestReason || undefined}>🚨 CANCEL REQUESTED</span>
              ) : null}
              <p className="text-[11.5px] text-txt-tertiary truncate mt-0.5">{c.user}</p>
            </div>
            <div className="text-right shrink-0 flex items-start gap-1">
              <div>
                <p className="text-[15px] font-bold text-txt-primary">{gbp(c.price)}</p>
                {c.paid && <p className={`text-[11px] font-semibold ${c.paidBad ? 'text-red-600' : 'text-emerald-600'}`}>{c.paid}</p>}
              </div>
              {onQualify && <QualifyBtn onClick={onQualify} />}
              {/* Re-fire the signup WhatsApp alert — trial columns only (Lee, 11 Aug 2026). */}
              {variant === 'trial' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    resendTrialAlert(c.deal.id)
                      .then((r) => crmToast(r.group ? `Signup alert re-sent — DM + "${r.group}"` : 'Signup alert re-sent (DM only — group not found)', 'success'))
                      .catch((err: any) => crmToast(err?.message || 'Could not resend the alert', 'error'))
                  }}
                  title="Re-send the signup WhatsApp alert (number + New Signups group)"
                  className="p-1 rounded-md border border-border text-txt-tertiary hover:text-emerald-700 hover:border-emerald-300 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73" /></svg>
                </button>
              )}
              <QuickBookButton dealId={c.deal.id} dealName={c.company} compact />
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: c.live ? '#10B981' : c.preTrial ? '#7C3AED' : '#1E293B' }}>
              {c.live ? 'LIVE' : c.preTrial ? 'ACTIVE LEAD' : 'ON TRIAL'}
            </span>
            <TrialDiscoveryBadge trialStartedAt={c.deal.trialStartedAt} discoveryCallDone={c.deal.discoveryCallDone} stage={c.deal.stage} />
            <LeadLocalTime tz={c.deal.localTz} country={c.deal.country} />
            <FormChip d={c.deal} /><FlagChips flags={c.deal.flags} />
            <DriveChip url={(c.deal as any).driveFolderUrl} />
            {/* Support history, joined live (Phase 2.1). An open ticket on a lead is
                commercial information — it belongs on the card, not just in Support. */}
            {c.support && c.support.openTickets > 0 && (
              <span
                className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white"
                style={{ background: c.support.openTickets >= 3 ? '#DC2626' : '#F59E0B' }}
                title={`${c.support.openTickets} open support ticket(s)`}
              >
                {c.support.openTickets} OPEN TICKET{c.support.openTickets > 1 ? 'S' : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {demo && (
        <div className="pt-1 space-y-2">
          {c.demoInfo ? (() => {
            const di = c.demoInfo
            // Is the person who booked a DIFFERENT person than the lead contact shown in the
            // header? If so, say it explicitly — that distinction is exactly what sales needs.
            const bookerDiffers = !!di.inviteeName && !!c.user && di.inviteeName.trim().toLowerCase() !== c.user.trim().toLowerCase()
            // One hour after the scheduled start the Meet link is pointless — drop the button,
            // keep the scheduled timings (the card sits below the HELD divider by then).
            const heldNow = !!c.deal.demoAt && Date.now() > new Date(c.deal.demoAt).getTime() + 3600_000
            return (
              <>
                <div className="rounded-lg border border-border bg-surface-secondary overflow-hidden">
                  {/* Meeting */}
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-[12px] font-semibold text-brand-600 truncate"><IconCalendarUi className="w-3.5 h-3.5 inline -mt-0.5" /> {di.eventName}</p>
                    <p className="text-[13px] font-semibold text-txt-primary mt-0.5">{di.when}</p>
                  </div>
                  {/* Booked by — first-class, unmistakable */}
                  <div className="px-3 py-2 flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-white text-[13px] font-bold" style={{ background: '#7C3AED' }}>
                      {(di.inviteeName || di.inviteeEmail || '?').charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">Booked by</p>
                      <p className="text-[13px] font-semibold text-txt-primary truncate">{di.inviteeName || di.inviteeEmail || 'Unknown'}</p>
                      {di.inviteeName && di.inviteeEmail && <p className="text-[11px] text-txt-tertiary truncate">{di.inviteeEmail}</p>}
                    </div>
                    {bookerDiffers && (
                      <span
                        className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide"
                        style={{ background: '#FEF3C7', color: '#92400E' }}
                        title={`Booked by ${di.inviteeName}, not the lead contact (${c.user})`}
                      >
                        Not the lead contact
                      </span>
                    )}
                  </div>
                </div>
                {!heldNow &&
                  (di.meetUrl ? (
                    <a
                      href={di.meetUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="block w-full py-2 rounded-lg text-sm font-semibold text-white text-center"
                      style={{ background: '#2F6FB0' }}
                    >
                      Join Google Meet
                    </a>
                  ) : (
                    <p className="text-[11px] text-txt-tertiary text-center">No meeting link on this booking</p>
                  ))}
              </>
            )
          })() : (
            <p className="text-center text-[13px] font-semibold text-brand-600">Demo Booked</p>
          )}
        </div>
      )}

      <div className="space-y-2.5">
        {!c.preTrial && <Row label="Onboarding Status" value={<OnbBadge onb={c.onb} />} />}
        {c.support && (
          <Row
            label="Support"
            value={
              <span className={c.support.openTickets > 0 ? 'text-red-600' : undefined}>
                {c.support.ticketsTotal > 0 ? `${c.support.ticketsTotal} ticket${c.support.ticketsTotal > 1 ? 's' : ''}` : 'No tickets'}
                {c.support.chatsTotal > 0 ? ` · ${c.support.chatsTotal} chat${c.support.chatsTotal > 1 ? 's' : ''}` : ''}
                {c.support.lastContactDays != null ? ` · ${agoLabel(c.support.lastContactDays)?.toLowerCase()}` : ''}
              </span>
            }
          />
        )}
        {c.live ? (
          <>
            <Row label="Go Live Date" value={<span className="text-brand-600">{c.goLive}</span>} />
            <Row label="Last Seen" value={c.lastUsage} />
            <Row label="Activity" value={c.avgUsage} />
            {c.adoption && <div className="pt-1"><ModuleStrip adoption={c.adoption} /></div>}
          </>
        ) : variant === 'trial_extended' ? (
          <>
            <Row label="Sign Up Date" value={c.signUp} />
            <Row label="First Trial End (14 Days)" value={<span className="text-orange-600">{c.firstTrialEnd}</span>} />
            <Row label="Latest Extension" value={c.latestExtension} />
            {c.reactEnded ? (
              <div className="text-[13px] text-red-600 font-medium">Reactivated Trial ENDED {c.reactEnded} · {c.reactEndedDaysAgo} Days Ago</div>
            ) : c.reactDaysLeft != null ? (
              <Row label="Reactivated Trial" value={<span className={c.reactDaysLeft <= 7 ? 'text-orange-600 font-medium' : undefined}>{c.reactDaysLeft === 0 ? 'Ends today' : `${c.reactDaysLeft} Days Left`}</span>} />
            ) : (
              <>
                <Row label={<span className="text-red-600">{c.totalExtensions} Total Extensions</span>} value={<span className="text-red-600">{(c.trialUsed ?? 0) - 14} Days</span>} />
                <Row label="Trial Time" value={`${c.trialUsed}/${c.trialTotal} Days`} />
                <div className="pt-1"><TrialBar used={c.trialUsed!} total={c.trialTotal!} firstTrial={14} /></div>
              </>
            )}
          </>
        ) : variant === 'trial_ended' ? (
          <>
            <div className="text-[13px] text-red-600 font-medium">Trial Ended {c.endedDate} · {c.endedDaysAgo} Days Ago</div>
            <Row label="Trial Time" value={`${c.trialUsed}/${c.trialTotal} Days`} />
            <div className="pt-1"><TrialBar used={c.trialUsed!} total={c.trialTotal!} ended /></div>
          </>
        ) : c.preTrial ? (
          <>
            <Row label="Source" value={c.deal.source || '—'} />
            <Row label="Added" value={c.signUp} />
            <Row label="Trial" value={<span className="text-txt-tertiary">Not started</span>} />
          </>
        ) : (
          <>
            <Row label="Sign Up Date" value={c.signUp} />
            {/* Reactivated trials render through THIS card (there is no separate
                trial-extended column) — 25 Aug 2026: without this branch they
                showed "undefined/undefined Days" (the AV Planners bug). */}
            {c.reactEnded ? (
              <div className="text-[13px] text-red-600 font-medium">Reactivated Trial ENDED {c.reactEnded} · {c.reactEndedDaysAgo} Days Ago</div>
            ) : c.reactDaysLeft != null ? (
              <Row label="Reactivated Trial" value={<span className={c.reactDaysLeft <= 7 ? 'text-orange-600 font-medium' : undefined}>{c.reactDaysLeft === 0 ? 'Ends today' : `${c.reactDaysLeft} Days Left`}</span>} />
            ) : c.trialUsed != null && c.trialTotal != null ? (
              <>
                <Row label="Trial Time" value={`${c.trialUsed}/${c.trialTotal} Days`} />
                <div className="pt-1"><TrialBar used={c.trialUsed} total={c.trialTotal} /></div>
              </>
            ) : (
              <Row label="Trial" value={<span className="text-txt-tertiary">No trial dates on record</span>} />
            )}
          </>
        )}
      </div>

      <TouchStrip deal={c.deal} company={c.company} />
      <NextTaskLine task={c.deal.nextTask} onOpenTasks={onOpenTasks} />
    </div>
  )
}

/**
 * One kanban column: a pinned coloured header, a pinned search box on top, the cards scrolling in
 * the middle, and a pinned counter + page-jumper glued to the bottom — so you can scroll the cards
 * and jump pages without either control moving. Search + paging are per-column and independent.
 */
const KanbanColumn: React.FC<{ col: Column; cards: LCard[]; onOpen: (d: Deal, tab?: 'tasks') => void; onQualify?: (d: Deal) => void; dropStage?: string; onDropCard?: (dealId: string, stage: string) => void; globalSearching?: boolean; onChanged?: () => void }> = ({ col, cards, onOpen, onQualify, dropStage, onDropCard, globalSearching, onChanged }) => {
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<SortKey>('newest')
  const [page, setPage] = useState(0)
  // Collapsible columns (Lee, 7 Aug 2026): chevron top-right; collapsed = a slim
  // strip with the padded count pill + vertical label. Remembered per column.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return !!JSON.parse(localStorage.getItem('wq_board_collapsed') || '{}')[col.key] } catch { return false }
  })
  const toggleCollapse = () => setCollapsed((v) => {
    const next = !v
    try {
      const m = JSON.parse(localStorage.getItem('wq_board_collapsed') || '{}')
      m[col.key] = next
      localStorage.setItem('wq_board_collapsed', JSON.stringify(m))
    } catch { /* remembering is best-effort */ }
    return next
  })
  // "See Pending" (Free Trial Sign Up only): show just the cards waiting on WeQuote —
  // Customer: Finished and WeQuote: In Progress.
  const hasPendingFilter = col.key === 'trials_qualifying' || col.key === 'trials_qualified' || col.key === 'free_trial_signup'
  const [pendingOnly, setPendingOnly] = useState(false)
  const pendingCount = useMemo(() => cards.filter((c) => c.onb === 'cust_done' || c.onb === 'wq_prog').length, [cards])

  const filtered = useMemo(() => {
    let base = cards
    if (hasPendingFilter && pendingOnly) base = base.filter((c) => c.onb === 'cust_done' || c.onb === 'wq_prog')
    const needle = q.trim().toLowerCase()
    if (!needle) return base
    return base.filter((c) => `${c.company} ${c.deal.company} ${c.deal.name} ${c.deal.contact} ${c.user}`.toLowerCase().includes(needle))
  }, [cards, q, hasPendingFilter, pendingOnly])

  // Demos Booked: upcoming demos on top (soonest first), then a HELD divider, then demos that
  // have already happened (most recent first). Held cards stay in this column until the lead
  // changes stage (Lee's ruling) — the divider is what separates "yet to be held" from "done".
  const isDemos = col.key === 'demos_qualifying' || col.key === 'demos_qualified' || col.key === 'demos'
  const { sorted, heldStart } = useMemo(() => {
    // "Date" sorts by the real activation date where we have it, falling back to when the lead was
    // added. Fewer "days ago" = more recent.
    const dateOf = (c: LCard) => (c.deal.activatedDays ?? c.deal.createdDays ?? 0)
    const nameOf = (c: LCard) => (c.company || c.deal.company || c.deal.name || '').toLowerCase()
    const cmp = (a: LCard, b: LCard): number => {
      switch (sort) {
        case 'oldest': return dateOf(b) - dateOf(a)
        case 'az': return nameOf(a).localeCompare(nameOf(b))
        case 'za': return nameOf(b).localeCompare(nameOf(a))
        case 'value': return (b.price || 0) - (a.price || 0)
        default: return dateOf(a) - dateOf(b) // newest
      }
    }
    if (!isDemos) return { sorted: [...filtered].sort(cmp), heldStart: -1 }
    const demoTime = (c: LCard) => (c.deal.demoAt ? new Date(c.deal.demoAt).getTime() : 0)
    // Held = one hour past the SCHEDULED start (Lee's rule) — same threshold that hides the
    // Meet button on the card. The card then sits below the HELD divider until the lead goes
    // on trial (stage change pulls it out of this column).
    const isHeld = (c: LCard) => {
      const s = demoTime(c)
      return !!s && Date.now() > s + 3600_000
    }
    const upcoming = filtered.filter((c) => !isHeld(c))
    const held = filtered.filter(isHeld)
    if (sort === 'newest' || sort === 'oldest') {
      // Default: order by the DEMO date — soonest upcoming on top; most recently held first.
      upcoming.sort((a, b) => demoTime(a) - demoTime(b))
      held.sort((a, b) => demoTime(b) - demoTime(a))
      if (sort === 'oldest') { upcoming.reverse(); held.reverse() }
    } else {
      upcoming.sort(cmp)
      held.sort(cmp)
    }
    return { sorted: [...upcoming, ...held], heldStart: upcoming.length }
  }, [filtered, sort, isDemos])

  useEffect(() => { setPage(0) }, [q, sort, pendingOnly])
  const pageCount = Math.max(1, Math.ceil(sorted.length / COLUMN_PAGE_SIZE))
  useEffect(() => { if (page > pageCount - 1) setPage(pageCount - 1) }, [pageCount, page])
  const clamped = Math.min(page, pageCount - 1)
  const pageCards = sorted.slice(clamped * COLUMN_PAGE_SIZE, clamped * COLUMN_PAGE_SIZE + COLUMN_PAGE_SIZE)
  const from = sorted.length === 0 ? 0 : clamped * COLUMN_PAGE_SIZE + 1
  const to = Math.min(sorted.length, (clamped + 1) * COLUMN_PAGE_SIZE)
  const pBtn = 'px-1.5 py-0.5 rounded text-[12px] font-bold text-txt-secondary hover:bg-surface-hover disabled:opacity-30 disabled:hover:bg-transparent'

  // Collapsed: a slim strip — chevron on top, the count in a padded pill so it
  // stays distinct, the column name running vertically. Click anywhere to expand.
  // UI audit (21 Aug): a GLOBAL search temporarily expands any collapsed column
  // that has matches — hidden hits behind a strip read as "no results". The
  // saved collapse preference is untouched; clearing the search re-collapses.
  if (collapsed && !(globalSearching && cards.length > 0)) {
    return (
      <div
        className="w-12 shrink-0 rounded-xl bg-surface-tertiary/40 border border-border flex flex-col overflow-hidden cursor-pointer hover:border-brand-300 transition-colors"
        onClick={toggleCollapse}
        title={`Expand ${col.label} (${cards.length})`}
      >
        <div className="h-11 shrink-0 flex items-center justify-center" style={{ background: col.color }}>
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </div>
        <div className="flex-1 min-h-0 flex flex-col items-center pt-3 gap-3">
          <span className="px-2 py-1 rounded-full text-[11px] font-bold border-2 bg-surface" style={{ color: col.color, borderColor: col.color }}>{cards.length}</span>
          <span className="text-[12px] font-bold text-txt-secondary tracking-wide whitespace-nowrap" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>{col.label}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="w-[370px] shrink-0 rounded-xl bg-surface-tertiary/40 border border-border flex flex-col overflow-hidden">
      {/* Coloured header (pinned) — padded count pill + collapse chevron */}
      <div className="px-4 py-3 shrink-0 flex items-center justify-between gap-2" style={{ background: col.color }}>
        <span className="text-[15px] font-bold text-white truncate">{col.label}</span>
        <span className="flex items-center gap-1.5 shrink-0">
          <span className="text-xs font-semibold text-white px-2 py-0.5 rounded-full bg-white/20">{filtered.length !== cards.length ? `${filtered.length} / ${cards.length}` : cards.length}</span>
          <button
            onClick={(e) => { e.stopPropagation(); toggleCollapse() }}
            title={`Collapse ${col.label}`}
            className="p-1 rounded-md hover:bg-white/20 transition-colors"
          >
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </button>
        </span>
      </div>
      {/* Search + sort (pinned top) */}
      <div className="px-2.5 py-2 shrink-0 border-b border-border bg-surface-tertiary/60 flex items-center gap-1.5">
        <SearchInput value={q} onChange={setQ} placeholder="Search this list…" className="flex-1 min-w-0" compact />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          title="Sort this list"
          className="shrink-0 rounded-lg border border-border bg-surface px-1.5 py-1.5 text-[11px] font-medium text-txt-secondary focus:outline-none focus:border-brand-400 cursor-pointer"
        >
          {SORT_LABELS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>
      {/* See Pending — Free Trial Sign Up only: Customer Finished + WeQuote In Progress. */}
      {hasPendingFilter && (
        <label className="px-2.5 py-1.5 shrink-0 border-b border-border bg-surface-tertiary/60 flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
          <span className="text-[11px] font-medium text-txt-secondary">
            See Pending <span className="text-txt-tertiary">— Customer: Finished · WeQuote: In Progress ({pendingCount})</span>
          </span>
        </label>
      )}
      {/* Cards (the only part that scrolls) */}
      <div
        data-col-body
        className="p-3 space-y-3 overflow-y-auto flex-1 min-h-0"
        onDragOver={dropStage ? (e) => e.preventDefault() : undefined}
        onDrop={dropStage && onDropCard ? (e) => { e.preventDefault(); const id = e.dataTransfer.getData('text/deal-id'); if (id) onDropCard(id, dropStage) } : undefined}
      >
        {pageCards.length === 0 ? (
          <p className="text-[11px] text-txt-tertiary text-center py-6">{q || globalSearching ? 'No matches in this list' : dropStage ? 'Drag cards here' : 'No companies'}</p>
        ) : (
          pageCards.map((c, idx) => {
            const draggable = c.deal.stage === 'free_trial_expired' || c.deal.stage === 'trial_expired_contacted'
            // HELD divider (Demos Booked): drawn before the first already-held card. If a page
            // starts inside the held section, the divider tops the page so the split stays clear.
            const gi = clamped * COLUMN_PAGE_SIZE + idx
            const showHeldDivider = heldStart >= 0 && heldStart < sorted.length && (gi === heldStart || (idx === 0 && gi > heldStart))
            return (
              <React.Fragment key={c.deal.id}>
                {showHeldDivider && (
                  <div className="flex items-center gap-2 pt-1" aria-label="Held demos below">
                    <span className="flex-1 h-px bg-border" />
                    <span className="text-[10px] font-bold tracking-widest text-txt-tertiary">HELD</span>
                    <span className="flex-1 h-px bg-border" />
                  </div>
                )}
                <div
                  draggable={draggable}
                  onDragStart={draggable ? (e) => e.dataTransfer.setData('text/deal-id', c.deal.id) : undefined}
                  className={`relative group ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
                  title={draggable ? 'Drag between Trial Expired and Trial Expired — Contacted' : undefined}
                >
                  {/* ARCHIVE ON THE CARD (Lee, 2 Nov 2026): hover control, top-right. */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      const archived = !!c.deal.archivedAt
                      if (!window.confirm(archived ? `Unarchive ${c.company}? It returns to every list, board and metric.` : `Archive ${c.company}? Hidden from every list, board and metric — restorable from the Archived column.`)) return
                      ;(archived ? restoreDeal(c.deal.id) : archiveDeal(c.deal.id))
                        .then(() => { crmToast(archived ? 'Lead restored' : 'Lead archived', 'success'); onChanged?.() })
                        .catch(() => crmToast('Failed', 'error'))
                    }}
                    title={c.deal.archivedAt ? 'Unarchive this lead' : 'Archive this lead'}
                    className="absolute top-1.5 right-1.5 z-10 px-1.5 py-0.5 rounded-md text-[10px] font-bold border border-border bg-surface/90 text-txt-tertiary opacity-0 group-hover:opacity-100 hover:text-txt-primary hover:border-border-strong transition-opacity"
                  >{c.deal.archivedAt ? '↩ Unarchive' : 'Archive'}</button>
                  <Card
                    c={c}
                    variant={col.variant}
                    onOpen={() => onOpen(c.deal)}
                    onOpenTasks={() => onOpen(c.deal, 'tasks')}
                    onQualify={onQualify && (col.key === 'demos_qualifying' || col.key === 'trials_qualifying') && c.deal.qualificationStatus !== 'qualified' ? () => onQualify(c.deal) : undefined}
                  />
                </div>
              </React.Fragment>
            )
          })
        )}
      </div>
      {/* Counter + page-jumper (pinned bottom) */}
      {filtered.length > COLUMN_PAGE_SIZE && (
        <div className="px-2.5 py-1.5 shrink-0 border-t border-border bg-surface-tertiary/60 flex items-center justify-between gap-1">
          <span className="text-[10px] text-txt-tertiary whitespace-nowrap">{from}–{to} of {filtered.length}</span>
          <div className="flex items-center gap-0.5">
            <button className={pBtn} onClick={() => setPage(0)} disabled={clamped === 0} title="First page">«</button>
            <button className={pBtn} onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={clamped === 0} title="Previous page">‹</button>
            <span className="text-[10px] font-semibold text-txt-primary px-1 whitespace-nowrap">{clamped + 1}/{pageCount}</span>
            <button className={pBtn} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={clamped >= pageCount - 1} title="Next page">›</button>
            <button className={pBtn} onClick={() => setPage(pageCount - 1)} disabled={clamped >= pageCount - 1} title="Last page">»</button>
          </div>
        </div>
      )}
    </div>
  )
}

export const CrmLifecycleBoard: React.FC<{ legacy?: boolean }> = ({ legacy }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [openDeal, setOpenDeal] = useState<Deal | null>(null)
  // Which tab the detail overlay opens on — 'tasks' when the card's Next Task footer was clicked.
  const [openTab, setOpenTab] = useState<'tasks' | undefined>(undefined)
  // QUALIFY POPUP (Lee, 9 Oct 2026) — opened from Qualifying-column cards.
  const [qualifyDeal, setQualifyDeal] = useState<Deal | null>(null)
  // includeTerminal: cancelled_subscription/closed_lost are TERMINAL stages, which
  // listDeals hides by default — without this flag the Cancelled/Churned column could
  // never receive a single deal (they showed on the Leads list, which passes it).
  const { data: deals, reload } = useCrm<Deal[]>(() => fetchAllDeals({ includeTerminal: true }), DEALS)
  // Archived (soft-deleted) records — separate query; they never mix into other columns' data.
  const { data: archivedDeals, reload: reloadArchived } = useCrm<Deal[]>(() => fetchAllDeals({ archived: true }), [])
  // Live board: the server refreshes from WeQuote every 2 minutes (auto-sync + events), so the
  // board quietly refetches every 60s — no manual page reloads needed to see status changes.
  useEffect(() => {
    const t = setInterval(() => { reload(); reloadArchived() }, 60_000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // One call for the whole board: real activity + last-seen per deal. Empty until
  // WeQuote pushes its first usage period — the cards then say "awaiting data"
  // instead of showing a fabricated hours figure.
  const { data: usage } = useCrm<UsageMap>(() => getUsageSummary() as Promise<UsageMap>, {})
  // Support history for every lead in one call (Phase 2.1) — tickets and chats
  // matched on contact email / company alias / wequote_account_id.
  const { data: support } = useCrm<SupportMap>(() => getSupportSummary() as Promise<SupportMap>, {})
  // Real module adoption (Phase 2.3) — replaces the strip that was coloured from the
  // characters of the deal id.
  const { data: adoption } = useCrm<AdoptionMap>(() => getAdoptionSummary() as Promise<AdoptionMap>, {})

  // The mouse wheel scrolls the cards vertically inside each column only — it
  // never scrolls the board horizontally. Horizontal is via the trackpad or the
  // scrollbar. (No wheel interception.)

  // GLOBAL board search (Lee, 20 Aug 2026): one bar filters EVERY column live —
  // matches land in their own columns, counts follow, empty columns say so.
  const [globalQ, setGlobalQ] = useState('')

  // Clock-wall timezone filter (Lee, 23 Sep 2026): a selected clock narrows
  // every column to leads in that zone group.
  const tzFilter = useTzFilter()
  const grouped = useMemo(() => {
    const needle = globalQ.trim().toLowerCase()
    const hit = (d: Deal) =>
      (!needle || `${d.company || ''} ${d.name || ''} ${d.contact || ''}`.toLowerCase().includes(needle)) &&
      matchesTzFilter(tzFilter, d.localTz)
    const g: Record<string, LCard[]> = {}
    const colDefs = legacy ? COLUMNS_LEGACY : COLUMNS
    colDefs.forEach((c) => (g[c.key] = []))
    deals.forEach((d) => {
      if (!hit(d)) return
      const col = legacy ? legacyColumnForDeal(d) : columnForDeal(d)
      if (col && g[col]) g[col].push(toCard(d, usage[d.id], support[d.id], adoption[d.id]))
    })
    // Archived column = the archived list, regardless of stage.
    g['archived_col'] = archivedDeals.filter(hit).map((d) => toCard(d, usage[d.id], support[d.id], adoption[d.id]))
    return g
  }, [deals, archivedDeals, usage, support, adoption, globalQ, tzFilter, legacy])

  // Drag-drop between Trial Expired ↔ Trial Expired — Contacted (manual sales state).
  const onDropCard = async (dealId: string, targetStage: string) => {
    try {
      await moveDealStage(dealId, targetStage)
      crmToast(targetStage === 'trial_expired_contacted' ? 'Marked as contacted' : 'Moved back to Trial Expired', 'success')
      reload()
    } catch {
      crmToast('Could not move the card', 'error')
    }
  }

  const boardCols = legacy ? COLUMNS_LEGACY : COLUMNS
  const total = boardCols.reduce((a, c) => a + (grouped[c.key]?.length || 0), 0)

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />
      {/* SIMPLE header (Lee, 23 Sep 2026): title + count + search on ONE line —
          the explanatory subtitle sentences are gone across the platform. */}
      <div className="px-6 py-3 bg-surface border-b border-border shrink-0 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-2 shrink-0">
          <h1 className="text-xl font-bold text-txt-primary">{legacy ? 'Sales Board' : 'Qualification Board'}</h1>
          <span className="text-sm text-txt-tertiary">{total} companies</span>
          {tzFilter && (
            <button
              onClick={() => setTzFilter(null)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand-50 text-brand-700 border border-brand-300 hover:bg-brand-100"
              title="Clear the timezone filter"
            >
              {tzFilter.label} time only ✕
            </button>
          )}
        </div>
        {/* GLOBAL search (Lee, 20 Aug 2026) — one big bar, filters every column
            live. House SearchInput (UI audit 21 Aug: was a one-off style). */}
        <SearchInput
          value={globalQ}
          onChange={setGlobalQ}
          placeholder="Search all cards — company or contact…"
          className="flex-1 min-w-[260px] max-w-[520px]"
        />
        {/* Alerts bell moved to the TopBar (Lee, 28 Oct 2026) — one bell, every page. */}
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden px-6 pt-4 pb-6">
        <div className="flex gap-5 items-stretch h-full">
          {boardCols.map((col) => (
            <KanbanColumn
              key={col.key}
              col={col}
              cards={grouped[col.key] || []}
              onOpen={(d, tab) => { setOpenTab(tab); setOpenDeal(d) }}
              onQualify={legacy ? undefined : (d) => setQualifyDeal(d)}
              dropStage={col.key === 'trial_ended' ? 'free_trial_expired' : col.key === 'trial_expired_contacted' ? 'trial_expired_contacted' : undefined}
              onDropCard={onDropCard}
              globalSearching={!!globalQ.trim()}
              onChanged={() => { reload(); reloadArchived() }}
            />
          ))}
        </div>
      </div>

      {/* QUALIFY POPUP (Lee, 9 Oct 2026): the full qualifying questions, then
          one green button to qualify the lead manually — it sticks. */}
      {qualifyDeal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setQualifyDeal(null)}>
          <div className="bg-surface rounded-2xl border border-border shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-border flex items-center justify-between shrink-0">
              <div>
                <p className="text-sm font-extrabold text-txt-primary">Qualify — {qualifyDeal.company || qualifyDeal.name}</p>
                <p className="text-[11px] text-txt-tertiary">Answer the qualifying questions, then press Qualify. A manual qualification sticks whatever the gates compute.</p>
              </div>
              <button onClick={() => setQualifyDeal(null)} className="text-txt-tertiary hover:text-txt-primary text-lg leading-none px-1">✕</button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              <DealQualification dealId={qualifyDeal.id} />
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 shrink-0">
              <button onClick={() => setQualifyDeal(null)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-secondary hover:bg-surface-hover">Close</button>
              <button
                onClick={() =>
                  patchQualification(qualifyDeal.id, { action: 'qualify' })
                    .then(() => { crmToast(`${qualifyDeal.company || qualifyDeal.name} qualified`, 'success'); setQualifyDeal(null); reload() })
                    .catch((e: any) => crmToast(e?.message || 'Could not qualify the lead', 'error'))
                }
                className="px-4 py-1.5 rounded-lg text-xs font-bold text-white"
                style={{ background: '#16A34A' }}
              >
                ✓ Qualify this lead
              </button>
            </div>
          </div>
        </div>
      )}

      {openDeal && <LeadRedirect deal={openDeal} tab={openTab} onDone={() => { setOpenDeal(null); setOpenTab(undefined) }} />}
    </div>
  )
}

/** SALES BOARD (Lee, 12 Oct 2026): the same board, pre-remodel column layout.
 *  Route: /crm/sales-board. One Qualified column, merged Demos Booked, trials
 *  not split by qualification, no Qualify button. */
export const CrmSalesBoard: React.FC = () => <CrmLifecycleBoard legacy />

